import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { Box, Divider, Table, TableBody, TableCell, TableContainer, TableRow, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { Group } from '@visx/group';
import { Treemap } from '@visx/hierarchy';
import { useParentSize } from '@visx/responsive';
import { useTooltip } from '@visx/tooltip';
import { hierarchy, treemapSquarify } from 'd3-hierarchy';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { roundNum } from '../../../helpers';
import Image from 'next/image';

const transformData = (data) => {
  const sectors = {};

  data.forEach(item => {
    const sectorName = item.sector || 'Other';
    const industryName = item.industry || 'Other';

    if (!sectors[sectorName]) {
      sectors[sectorName] = { name: sectorName, children: {} };
    }
    if (!sectors[sectorName].children[industryName]) {
      sectors[sectorName].children[industryName] = { name: industryName, children: [] };
    }
    sectors[sectorName].children[industryName].children.push(item);
  });

  return {
    name: 'root',
    children: Object.values(sectors).map(sector => ({
      ...sector,
      children: Object.values(sector.children),
    })),
  };
};

const getTickerFontSize = (value) => {
  const v = parseFloat(value);
  if (v >= 30) return 30;
  if (v >= 20) return 27;
  if (v >= 15) return 20;
  if (v >= 12) return 15;
  if (v >= 9) return 12;
  if (v >= 6) return 10;
  return 9;
};

const getPctFontSize = (value) => {
  const v = parseFloat(value);
  if (v >= 20) return 20;
  if (v >= 16) return 20;
  if (v >= 8) return 16;
  if (v >= 5) return 10;
  return 9;
};

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    boxShadow: theme.shadows[1],
    fontSize: 12,
    fontWeight: '600 !important',
    cursor: "pointer"
  },
}));

const HeatTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#fff',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 377,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
    padding: 0,
    borderRadius: '8px',
    boxShadow: '0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)',
  },
}));

const SvgContainer = styled('div')({
  width: '100%',
  height: '100%',
  position: 'relative',
  overflow: 'hidden',
  padding: '7px 7px 0 7px',
  backgroundColor: '#fbfbfc',
  '& .visx-group.node:hover rect': {
    opacity: 0.7,
  },
  '& .skeleton:after': {
    animation: '2s linear 0.5s infinite normal none running animation-wiooy9',
    background: 'linear-gradient(90deg, transparent, rgb(0 0 0 / 9%), transparent)',
    content: "''",
    position: 'absolute',
    transform: 'translateX(-100%)',
    inset: '0px',
  }
});

const StocksHeatMap = ({ data, isLoading, heatmapColorSettings }) => {
  const [colorMetric, setColorMetric] = useState('PCHG__1');
  const [sizeMetric, setSizeMetric] = useState('MKTCAP');
  const mult = colorMetric === "PCHG__1" ? 1 : 100;

  const { parentRef, width } = useParentSize({ debounceTime: 150 });
  const { tooltipData, showTooltip, hideTooltip } = useTooltip();

  const { colors } = useMemo(() => {
    if (!heatmapColorSettings) return { colors: [] };
    try {
      return JSON.parse(heatmapColorSettings);
    } catch (e) {
      console.error('Failed to parse heatmapColorSettings:', e);
      return { colors: [] };
    }
  }, [heatmapColorSettings]);

  const getBGColor = (value, metric) => {
    if (isLoading) return 'rgb(57, 57, 57)';
    const result = colors?.find((d) => d.type === metric?.replace("__1", "") && parseFloat(value) >= d.min && parseFloat(value) <= d.max);
    if (result) return result.color;

    const parsedValue = parseFloat(value);
    if (parsedValue > 0) return 'rgb(49, 85, 49)';
    if (parsedValue < 0) return 'rgb(115, 32, 32)';
    return 'rgb(57, 57, 57)';
  };

  const getStrokeColor = () => {
    if (isLoading) return 'rgb(57, 57, 57)';
    return '#fbfbfc7a';
  };

  const root = useMemo(() => {
    if (!data || data.length === 0) return hierarchy({ name: 'root', children: [] });
    
    const hierarchyData = transformData(data);
    return hierarchy(hierarchyData)
      .sum(d => (d[sizeMetric] ? d[sizeMetric] : 0))
      .sort((a, b) => b.value - a.value);
  }, [data, sizeMetric]);

  const handleMouseEnter = (nodeData) => {
    if (tooltipData?.ticker !== nodeData.ticker) {
      showTooltip({ tooltipData: nodeData });
    }
  };

  return (
    <Box component="div" sx={{ bgcolor: '#fbfbfc', boxShadow: 0, pt: 1, border: 1, borderColor: 'rgb(229, 234, 242)', borderRadius: '7px', mt: 2, mb: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: 'rgb(229, 234, 242)', flexWrap: { md: 'nowrap', sm: 'wrap', xs: 'wrap' } }}>
        <Box component='div' sx={{ pt: 0, mr: 2, pb: 1, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: "space-between", width: '100%' }}>
          <Typography color="primary" variant="h6" sx={{ mx: 2, fontWeight: 600, fontSize: '1rem', lineHeight: '1.6', letterSpacing: '0.0075em' }}>
            S&P 500 Performance
          </Typography>
          <LightTooltip title="This heatmap visually displays market capitalization and recent percentage changes, enabling swift identification of market and significant movements.">
            <InfoRoundedIcon />
          </LightTooltip>
        </Box>
      </Box>
      <SvgContainer ref={parentRef} className="heat-map">
        {isLoading && <span className="skeleton" style={{ display: 'block', backgroundColor: 'rgba(0, 0, 0, 0.11)', position: 'relative', maskImage: ' -webkit-radial-gradient(center, white, black)', height: '100%', overflow: 'hidden' }} />}
        <Treemap
          root={root}
          size={[width || 0, 577]}
          tile={treemapSquarify}
          round={false}
        >
          {treemap => (
            <HeatTooltip
              slotProps={{
                popper: {
                  sx: {
                    [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]: {
                      marginTop: '10px',
                      marginRight: '17px',
                    },
                  },
                },
              }}
              title={tooltipData ?
                <React.Fragment>
                  <Box component="div" sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box component="div">
                      <Box component="div" sx={{ bgcolor: '#fbfbfc', borderRadius: '7px 7px 0 0', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'start', color: '#333', p: 1 }}>
                        <Box component="div">
                          <Typography variant="body1" fontWeight="bold">
                            {tooltipData.ticker}
                          </Typography>
                          <Typography variant="body2" component="div" color="#555">{tooltipData.name}</Typography>
                        </Box>
                        <Image
                          itemProp="logo" alt={`${tooltipData?.ticker} logo`}
                          src={`https://logo.grufity.com/${tooltipData?.ticker}.png`}
                          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                          quality={50}
                          priority={false}
                          loading='lazy'
                          placeholder='empty'
                          width={37}
                          height={37}
                          style={{
                            height: 'auto',
                            marginLeft: '16px', marginTop: '2px', background: '#f8f9fa', padding: .1,
                            border: '1px solid rgb(218,220,224)', borderRadius: "5px", boxShadow: 0, color: '#174c86',
                            objectFit: 'contain', display: 'block'
                          }}
                        />
                      </Box>
                      <Box>
                        <Divider sx={{ mb: .5, borderColor: 'rgb(229, 234, 242)' }} />
                        <TableContainer>
                          <Table size="small" aria-label="performance overview tooltip table">
                            <TableBody sx={{ '& td, th': { border: 0, color: "#444", whiteSpace: 'nowrap', px: 1 } }} >
                              <TableRow>
                                <TableCell component="th" scope="row" sx={{ py: .2 }}>
                                  {colorMetric === "PCHG__365" ? `1-Year Pct. Chg.` :
                                    colorMetric === "PCHG__30" ? `30-Day Pct. Chg.` :
                                      colorMetric === "PCHG__7" ? `7-Day Pct. Chg.` :
                                        `Day Pct. Chg.`}
                                </TableCell>
                                <TableCell align="right" sx={{ py: .2 }}>
                                  <Box component="span" sx={{ fontWeight: 'bold', color: tooltipData[colorMetric] > 0 ? 'green' : 'red' }}>
                                    {tooltipData[colorMetric] != null ? `${roundNum(tooltipData[colorMetric])}%` : 'N/A'}
                                  </Box>
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell component="th" scope="row" sx={{ py: .2 }}>Sector</TableCell>
                                <TableCell align="right" sx={{ py: .2, fontWeight: 'bold' }}>{tooltipData.sector}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell component="th" scope="row" sx={{ py: .2 }}>Industry</TableCell>
                                <TableCell align="right" sx={{ py: .2, fontWeight: 'bold' }}>{tooltipData.industry}</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    </Box>
                  </Box>
                </React.Fragment>
                : ''}
              placement="bottom-end"
              followCursor={true}
              leaveDelay={300}
              disableInteractive
            >
              <svg 
                width={Math.floor(width) || '100%'} 
                height={577} 
                onMouseLeave={hideTooltip}
                role="graphics-document" 
                aria-label="S&P 500 Performance Heatmap"
              >
                <Group>
                  {treemap
                    .descendants()
                    .reverse()
                    .map((node) => {
                      const nodeKey = node.data.ticker || node.data.name || `node-${node.x0}-${node.y0}`;
                      
                      return (
                        <Group key={nodeKey} top={node.y0} left={node.x0}>
                          {node.depth === 1 &&
                            <rect
                              width={node.x1 - node.x0}
                              height={node.y1 - node.y0}
                              fill="transparent"
                              stroke={"#fbfbfc"}
                              strokeWidth={5}
                              rx={2}
                              style={{ pointerEvents: 'none' }}
                            />}
                          {node.depth === 2 &&
                            <rect
                              width={node.x1 - node.x0}
                              height={node.y1 - node.y0}
                              fill="transparent"
                              stroke={"#fbfbfc"}
                              strokeWidth={2}
                              rx={1}
                              style={{ pointerEvents: 'none' }}
                            />
                          }
                          {node.depth > 2 &&
                            <rect
                              width={node.x1 - node.x0}
                              height={node.y1 - node.y0}
                              fill={getBGColor(node.data[colorMetric], colorMetric)}
                              stroke={getStrokeColor()}
                              rx={1}
                              style={{ cursor: 'pointer' }}
                              onClick={() => window.open(`/stock/${node.data.ticker}`, '_blank', 'noopener,noreferrer')}
                              onMouseEnter={() => handleMouseEnter(node.data)}
                              onMouseLeave={hideTooltip}
                            />
                          }
                          {node.data['ticker'] ? (
                            <text
                              x={(node.x1 - node.x0) / 2}
                              y={(node.y1 - node.y0) / 2}
                              dy={(node.x1 - node.x0) < 30 ? "-0.2em" : "-0.4em"}
                              textAnchor="middle"
                              fontSize={getTickerFontSize((Math.min((node.x1 - node.x0), (node.y1 - node.y0)) / 6).toFixed(2))}
                              fontWeight="bold"
                              fill="#fff"
                              style={{ pointerEvents: 'none' }}
                              transform={(node.x1 - node.x0) < 30 ? `rotate(90, ${(node.x1 - node.x0) / 2}, ${(node.y1 - node.y0) / 2})` : ''}
                            >
                              {node.data.ticker}
                            </text>
                          ) : null}
                          {node.data[colorMetric] != null && node.data[colorMetric] !== '' ? (
                            <text
                              x={(node.x1 - node.x0) / 2}
                              y={(node.y1 - node.y0) / 2}
                              dy={(node.x1 - node.x0) < 30 ? ".87em" : "1em"}
                              fontSize={getPctFontSize((Math.min((node.x1 - node.x0), (node.y1 - node.y0)) / 10).toFixed(2))}
                              fontWeight="bold"
                              textAnchor="middle"
                              fill="#fff"
                              style={{ pointerEvents: 'none' }}
                              transform={(node.x1 - node.x0) < 30 ? `rotate(90, ${(node.x1 - node.x0) / 2}, ${(node.y1 - node.y0) / 2})` : ''}
                            >
                              {roundNum(parseFloat(node.data[colorMetric]) * mult, 2)}%
                            </text>
                          ) : null}
                        </Group>
                      );
                    })}
                </Group>
              </svg>
            </HeatTooltip>
          )}
        </Treemap>
      </SvgContainer>
    </Box>
  );
};

StocksHeatMap.propTypes = {
  data: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  heatmapColorSettings: PropTypes.string,
};

export default StocksHeatMap;